/* eslint-disable no-console */
import * as Sentry from '@sentry/browser';
import { BrowserTracing } from '@sentry/tracing';
import { ExternalService } from './constants';
import config from '../config';
import { initUnhandledRejectionHandler } from './reportUnhandledRejection';

const isDebug = !!config.enableLogs;

const apiOrigins = [config.apiUrl, config.drmApiUrl, config.cashflowApiUrl, config.admin]
  .filter(Boolean)
  .map(url => {
    try {
      return new URL(url).origin;
    } catch {
      return null;
    }
  })
  .filter(Boolean);

const tracePropagationTargets = ['localhost', /^\//, ...new Set(apiOrigins)];

let logLevel = '0';

const logger = (loggerType, ...args) => {
  if (isDebug && logLevel === '1') {
    return console[loggerType](...args);
  }
  return () => {};
};

function logError(message, info = {}, severity = 'error') {
  // Throw and catch error to get a stack trace
  try {
    throw new Error(message);
  } catch (e) {
    console.error(e);
    Sentry.withScope(scope => {
      scope.setLevel(severity);
      scope.setExtras(info);
      Sentry.captureException(e);
    });
  }
}

const logging = {
  debug: (...args) => logger('debug', ...args),
  error: (...args) => logger('error', ...args),
  info: (...args) => logger('info', ...args),
  log: (...args) => logger('log', ...args),
  trace: (...args) => logger('trace', ...args),
  warn: (...args) => logger('warn', ...args),
};

const initSentry = () => {
  Sentry.init({
    dsn: ExternalService.sentryDsn,
    environment: config.sentryEnvironment,
    release: config.version,
    integrations: [new BrowserTracing({ tracePropagationTargets })],
    beforeSend(evt, hint) {
      evt.extra = { ...evt.extra, ...hint.originalException?.info };
      return evt;
    },
    ignoreUrls: [
      'https://js.hsadspixel.net/fb.js',
      'https://telemetry.ext.platformdevelopment.xero.com/',
      'js.hsadspixel.net',
    ],
    ignoreErrors: [
      'Network Error',
      /ResizeObserver/,
      "Reached plan's maximum number of senders",
      /could not be updated in Xero$/,
      'Request aborted',
      'Unable to add domain',
      'No contacts match the message sender email',
      'User logged out',
      'Password not set for',
      'File size needs to be 150KB or less.',
      'Message already marked as chase-related',
      'Blocked',
      `Blocked 'script' from 'js.hsadspixel.net'`,
      `Blocked 'script' from 'eval:'`,
      `Blocked 'script' from 'inline:'`,
      `Blocked 'font' from 'static2.sharepointonline.com'`,
      /telemetry.ext.platformdevelopment.xero.com/,
    ],
  });
};

const configureSentry = (user, shortCode) => {
  Sentry.configureScope(scope => {
    if (user.isLoggedIn) {
      scope.setUser({
        id: user.userId,
      });
      scope.setTag('shortCode', shortCode);
    } else {
      scope.setUser(null);
      scope.setTag('shortCode', null);
    }
  });
};

let LOGGER = {
  init: initSentry,
  initialised: false,
  configure: configureSentry,
  configured: false,
  name: 'sentry',
};

const useConfigureLogging = ({ shortCode, user }) => {
  if (!LOGGER.initialised) {
    console.error(`configuration error: ${LOGGER.name} is not initialised`);
  } else if (!LOGGER.configured) {
    configureSentry(user, shortCode);
    LOGGER.configured = true;
  }
};

function initLogging() {
  if (isDebug) {
    const urlParams = new URLSearchParams(window.location.search);
    logLevel = urlParams.get('logLevel') || '0';
    console.log('Logging level:', logLevel);
  }

  // Lets support know the exact build a user is on: type $VERSION in the browser console.
  window.$VERSION = config.version;

  LOGGER.init();
  LOGGER.initialised = true;
  initUnhandledRejectionHandler();
}

const pendo = {
  track: (name, opts) => window.pendo && window.pendo.track(name, opts),
  trackAgent: (eventType, properties) =>
    window.pendo && typeof window.pendo.trackAgent === 'function'
      ? window.pendo.trackAgent(eventType, properties)
      : undefined,
};

const AGENT_CONTENT_MAX_LENGTH = 500;

const trackAgentEvent = (eventType, metadata = {}) => {
  try {
    const trackAgentApi = window.pendo && window.pendo.trackAgent;
    if (typeof trackAgentApi !== 'function') return;

    const agentId = config.pendoAgentId;

    const safeContent = String(metadata.content || '').slice(0, AGENT_CONTENT_MAX_LENGTH);
    if (!agentId || !metadata.conversationId || !metadata.messageId || !safeContent) {
      return;
    }

    const payload = {
      ...metadata,
      agentId,
      content: safeContent,
    };

    trackAgentApi(eventType, payload);
  } catch {
    // Analytics failures must never break the product.
  }
};

export { useConfigureLogging, initLogging, logging, logError, pendo, trackAgentEvent };
